import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Clock, MapPin, Zap, Check, Heart, ShieldCheck, Users, MessageCircle, ChevronLeft, Wallet, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, currencySymbol, whatsappLink, logError, PLACEHOLDER_IMG } from "@/lib/apiClient";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { MapEmbed } from "@/components/MapEmbed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";

export default function ActivityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, login } = useAuth();
  const [activity, setActivity] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [fav, setFav] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [guests, setGuests] = useState(2);
  const [booking, setBooking] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = useCallback(() => {
    return api.get(`/activities/${id}`)
      .then((r) => setActivity(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else logError("activity load failed", e);
      });
  }, [id]);
  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);
  useEffect(() => { if (user?.favorites) setFav(user.favorites.includes(id)); }, [user, id]);

  const toggleFav = async () => {
    if (!user) return login();
    const r = await api.post(`/favorites/${id}`);
    setFav(r.data.favorited);
    toast.success(r.data.favorited ? "Added to favorites" : "Removed from favorites");
  };

  const createBooking = async (method) => {
    if (!user) return login();
    if (!date) return toast.error("Please select a date");
    setBooking(true);
    try {
      const b = await api.post("/bookings", { activity_id: id, date, time, guests, payment_method: method });
      if (method === "cash") {
        toast.success(t("cash_booked"));
        navigate("/dashboard");
        return;
      }
      const c = await api.post("/payments/checkout", { booking_id: b.data.id, origin_url: window.location.origin });
      window.location.href = c.data.checkout_url;
    } catch (e) {
      logError("booking failed", e);
      toast.error(e?.response?.data?.detail || "Could not complete booking. Please try again.");
      setBooking(false);
    }
  };

  const submitReview = async () => {
    if (!user) return login();
    try {
      await api.post(`/activities/${id}/reviews`, { rating, comment });
      toast.success("Thanks for your review!");
      setReviewOpen(false); setComment("");
      load();
    } catch (e) { toast.error("Could not submit review"); }
  };

  if (notFound) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6 text-center" data-testid="activity-not-found">
        <div>
          <h1 className="font-display text-2xl font-semibold mb-2">Experience not found</h1>
          <p className="text-muted-foreground mb-5">This activity may have been removed or the link is incorrect.</p>
          <Button onClick={() => navigate("/activities")} className="rounded-full">Browse activities</Button>
        </div>
      </div>
    );
  }

  if (!activity) {
    return <div className="max-w-7xl mx-auto px-6 py-8"><Skeleton className="h-[420px] rounded-3xl" /></div>;
  }

  const total = (activity.price * guests).toFixed(2);
  const sym = currencySymbol(activity.currency);
  const gallery = activity.images?.length ? activity.images : [PLACEHOLDER_IMG];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      {/* Gallery */}
      <div className="grid md:grid-cols-[1fr_320px] gap-3">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative rounded-3xl overflow-hidden aspect-[16/10]">
          <img src={gallery[activeImg] || gallery[0]} alt={activity.title} className="h-full w-full object-cover" />
          <button data-testid="fav-btn" onClick={toggleFav}
            className="absolute top-4 right-4 grid place-items-center h-11 w-11 rounded-full glass hover:scale-105 transition-transform">
            <Heart className={`h-5 w-5 ${fav ? "fill-accent text-accent" : ""}`} />
          </button>
        </motion.div>
        <div className="grid grid-cols-4 md:grid-cols-1 gap-3">
          {gallery.map((img, i) => (
            <button key={img} data-testid={`thumb-${i}`} onClick={() => setActiveImg(i)}
              className={`rounded-2xl overflow-hidden aspect-square md:aspect-[16/10] border-2 ${activeImg === i ? "border-primary" : "border-transparent"}`}>
              <img src={img} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-[1fr_380px] gap-8">
        {/* Content */}
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-accent">{activity.category?.replace("-", " ")}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{activity.title}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
            <span className="flex items-center gap-1 font-semibold"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {activity.rating} <span className="text-muted-foreground font-normal">({activity.review_count} {t("reviews")})</span></span>
            <span className="flex items-center gap-1 text-muted-foreground"><Clock className="h-4 w-4" /> {activity.duration}</span>
            <span className="flex items-center gap-1 text-muted-foreground"><MapPin className="h-4 w-4" /> {activity.location.name}</span>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {activity.instant_booking && <Badge icon={Zap} text={t("instant")} />}
            <Badge icon={ShieldCheck} text={t("verified")} />
            <Badge icon={Users} text={`Max ${activity.max_guests}`} />
          </div>

          <p className="mt-6 text-base leading-relaxed text-foreground/80">{activity.description}</p>

          {/* Inclusions */}
          <h2 className="font-display text-xl font-semibold mt-8 mb-3">{t("inclusions")}</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {activity.inclusions.map((inc) => (
              <div key={inc} className="flex items-center gap-2 text-sm">
                <span className="grid place-items-center h-6 w-6 rounded-full bg-primary/10 text-primary shrink-0"><Check className="h-3.5 w-3.5" /></span>
                {inc}
              </div>
            ))}
          </div>

          {/* Pickup */}
          <h2 className="font-display text-xl font-semibold mt-8 mb-2">{t("pickup")}</h2>
          <p className="text-sm text-muted-foreground">{activity.pickup_info}</p>

          {/* Map */}
          <h2 className="font-display text-xl font-semibold mt-8 mb-3">{t("location")}</h2>
          <MapEmbed location={activity.location} />

          {/* Reviews */}
          <div className="flex items-center justify-between mt-10 mb-3">
            <h2 className="font-display text-xl font-semibold">{t("reviews")} ({activity.review_count})</h2>
            <Button data-testid="write-review-btn" variant="outline" size="sm" className="rounded-full" onClick={() => (user ? setReviewOpen(true) : login())}>
              {t("write_review")}
            </Button>
          </div>
          <div className="space-y-3">
            {(activity.reviews || []).length === 0 && <p className="text-sm text-muted-foreground">Be the first to review this experience.</p>}
            {(activity.reviews || []).map((r) => (
              <div key={r.id} className="rounded-2xl bg-white border border-black/5 p-4" data-testid="review-item">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{r.user_name}</span>
                  <span className="flex items-center gap-0.5">{[...Array(r.rating)].map((_, i) => <Star key={`${r.id}-star-${i}`} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}</span>
                </div>
                {r.comment && <p className="text-sm text-muted-foreground mt-1.5">{r.comment}</p>}
              </div>
            ))}
          </div>
        </div>

        {/* Booking card */}
        <div>
          <div className="lg:sticky lg:top-20 rounded-3xl bg-white border border-black/5 soft-shadow p-5">
            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-sm text-muted-foreground">{t("from")}</span>
              <span className="font-display text-3xl font-bold">{sym}{activity.price}</span>
              <span className="text-sm text-muted-foreground">/ {t("per_person")}</span>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-sm">{t("select_date")}</Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="booking-date" className="mt-1 w-full flex items-center gap-2 rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-left hover:bg-secondary transition-colors">
                      <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
                      {date ? date : <span className="text-muted-foreground">{t("select_date")}</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date ? new Date(date + "T00:00:00") : undefined}
                      onSelect={(d) => {
                        if (d) {
                          setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
                          setDateOpen(false);
                        }
                      }}
                      disabled={[{ before: new Date() }, ...(activity.blocked_dates || []).map((s) => new Date(s + "T00:00:00"))]}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">{t("select_time")}</Label>
                  <Input data-testid="booking-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sm">{t("guests")}</Label>
                  <Input data-testid="booking-guests" type="number" min={1} max={activity.max_guests} value={guests}
                    onChange={(e) => setGuests(Math.max(1, Math.min(activity.max_guests, parseInt(e.target.value) || 1)))} className="mt-1 rounded-xl" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-black/5">
              <span className="font-medium">{t("total")}</span>
              <span className="font-display text-2xl font-bold" data-testid="booking-total">{sym}{total}</span>
            </div>

            <Button data-testid="book-now-btn" onClick={() => createBooking("online")} disabled={booking}
              className="w-full mt-4 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 text-base">
              {booking ? "Processing..." : (user ? t("pay_online") : t("login_prompt"))}
            </Button>

            {user && (
              <Button data-testid="book-cash-btn" onClick={() => createBooking("cash")} disabled={booking} variant="outline"
                className="w-full mt-2 rounded-full h-12 text-base gap-2">
                <Wallet className="h-4 w-4" /> {t("pay_cash")}
              </Button>
            )}

            <a data-testid="wa-operator" href={whatsappLink(`Hi! I'm interested in "${activity.title}" in Alanya.`)} target="_blank" rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <MessageCircle className="h-4 w-4" /> Ask the operator on WhatsApp
            </a>

            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" /> Secure payment · {t("free_cancel")}
            </div>
          </div>
        </div>
      </div>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("write_review")}</DialogTitle></DialogHeader>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}>
                <Star className={`h-7 w-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
              </button>
            ))}
          </div>
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience..." className="rounded-xl" />
          <Button data-testid="submit-review-btn" onClick={submitReview} className="rounded-full">Submit review</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Badge = ({ icon: Icon, text }) => (
  <span className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold">
    <Icon className="h-3.5 w-3.5 text-primary" /> {text}
  </span>
);
